import React, { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../utils/tracking';

// "Así se ve H.E.R. por dentro": video de demostración vertical (9:16, ~20 s) hecho con capturas de las pantallas
// reales del producto. Sin controles, sin botón de play y sin textos superpuestos.
//
// Comportamiento:
//  - muted + loop + playsInline, sin controles visibles.
//  - No descarga nada hasta que el bloque está cerca de la pantalla (300 px); se reproduce cuando entra en
//    viewport (≥35 % visible) y se pausa al salir.
//  - "Reducir movimiento" NO lo bloquea: es un video mudo, corto, sin destellos ni parpadeos, y en muchos celulares
//    ese ajuste viene activado (ahorro de batería, accesibilidad) y dejaba solo la imagen. Solo el modo "ahorro de
//    datos" lo deja como póster; ahí un toque sobre el video lo reproduce.
//  - Si el navegador bloquea la reproducción automática, reintenta en cuanto la persona toca, desliza o hace scroll.
//  - Un toque sobre el video lo pausa o lo reanuda; si se pausó, no se reanuda solo.
//  - Si el video falla, queda el póster quieto. Un corte de red se reintenta antes de rendirse.
//  - Diagnóstico: ?videodebug=1 muestra una línea chica con el estado (incluye ahorro de datos / reducir movimiento
//    y el motivo si play() fue rechazado).
const R2 = 'https://archivos.simpleymejor.com/';
export const DEMO_VIDEO_SOURCES = [R2 + 'httpsarchivos.simpleymejor.comher-demo-video-1080x1920.mp4.mp4', R2 + 'her-demo-video-1080x1920.mp4'];
export const DEMO_VIDEO_POSTERS = [R2 + 'httpsarchivos.simpleymejor.comher-demo-video-poster.jpg.jpg', R2 + 'httpsarchivos.simpleymejor.comher-demo-video-poster.jpg', R2 + 'her-demo-video-poster.jpg'];
const ALT = 'Demostración en video de H.E.R. por dentro: el recorrido de 21 días, el Recetario, Movement, Noche en Calma, el Tracker, la Guía y H.E.R. Dulce.';
export function HerDemoVideo({
  sources = DEMO_VIDEO_SOURCES,
  posters = DEMO_VIDEO_POSTERS
}: {
  sources?: string[];
  posters?: string[];
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const inViewRef = useRef(false);
  const userPausedRef = useRef(false);
  const playedOnceRef = useRef(false);
  const blockedRef = useRef(false);
  const [armed, setArmed] = useState(false);
  const [failed, setFailed] = useState(false);
  const [srcIdx, setSrcIdx] = useState(0);
  const [poster, setPoster] = useState<string | undefined>(undefined);
  const [dbg, setDbg] = useState('');
  const retryRef = useRef(0);
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('videodebug');
  const reducedMotion = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = typeof navigator !== 'undefined' && (navigator as any).connection?.saveData === true;
  const note = (label: string) => {
    if (!debug) return;
    const v = videoRef.current;
    setDbg(`${label} · fuente ${srcIdx + 1}/${sources.length} · readyState ${v ? v.readyState : '-'} · networkState ${v ? v.networkState : '-'} · error ${v && v.error ? v.error.code : 'ninguno'} · reducirMovimiento ${reducedMotion ? 'sí' : 'no'} · ahorroDatos ${saveData ? 'sí' : 'no'}`);
  };
  // Solo el ahorro de datos deja el video como póster (con toque para reproducir).
  const [staticOnly] = useState(() => saveData);
  const tryPlay = (force = false) => {
    const v = videoRef.current;
    if (!v || staticOnly && !force || userPausedRef.current || !inViewRef.current) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        blockedRef.current = false;
      }).catch((err: any) => {
        if (err && err.name === 'AbortError') return;
        blockedRef.current = true;
        note('play() rechazado: ' + (err && err.name ? err.name : 'desconocido'));
      });
    }
  };
  const handleError = () => {
    const v = videoRef.current;
    const code = v && v.error ? v.error.code : 0;
    trackEvent('demo_video_error', {
      code,
      source_index: srcIdx
    });
    note('ERROR');
    if (v && code === 2 && retryRef.current < 2) {
      retryRef.current += 1;
      window.setTimeout(() => {
        v.load();
        tryPlay();
      }, 700 * retryRef.current);
      return;
    }
    retryRef.current = 0;
    if (srcIdx + 1 < sources.length) setSrcIdx(srcIdx + 1);else setFailed(true);
  };
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (typeof IntersectionObserver === 'undefined') {
      setArmed(true);
      return;
    }
    const near = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setArmed(true);
        near.disconnect();
      }
    }, {
      rootMargin: '300px 0px'
    });
    near.observe(wrap);
    const vis = new IntersectionObserver(entries => {
      const e = entries[entries.length - 1];
      inViewRef.current = e.isIntersecting;
      const v = videoRef.current;
      if (!v) return;
      if (e.isIntersecting) tryPlay();else v.pause();
    }, {
      threshold: 0.35
    });
    vis.observe(wrap);
    return () => {
      near.disconnect();
      vis.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticOnly]);
  useEffect(() => {
    if (staticOnly) return;
    const onGesture = () => {
      const v = videoRef.current;
      if (!v || !blockedRef.current || !v.paused) return;
      tryPlay();
    };
    const evs = ['touchstart', 'touchend', 'pointerup', 'click', 'keydown', 'scroll'];
    evs.forEach(e => window.addEventListener(e, onGesture, {
      passive: true
    }));
    return () => evs.forEach(e => window.removeEventListener(e, onGesture));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticOnly]);
  useEffect(() => {
    if (!armed) return;
    let cancelled = false;
    const probe = (i: number) => {
      if (i >= posters.length || cancelled) return;
      const img = new Image();
      img.onload = () => {
        if (!cancelled) setPoster(posters[i]);
      };
      img.onerror = () => probe(i + 1);
      img.src = posters[i];
    };
    probe(0);
    return () => {
      cancelled = true;
    };
  }, [armed, posters]);
  if (failed && !poster) return null;
  return <div className="text-center" data-id="element-149" data-mp="3gl7awxzeiv7">
      <p className="uppercase tracking-[0.22em] text-[#C8A870]" style={{
      fontSize: '12.5px',
      fontFamily: 'sans-serif',
      fontWeight: 600
    }} data-id="element-150" data-mp="7gvm8z3r4jm7">
        Así se ve H.E.R. por dentro
      </p>
      <p className="mx-auto mt-3 max-w-md font-body text-[14.5px] leading-relaxed text-[#F0E8D8]/75" data-id="element-151" data-mp="hfb6qj89wumq">
        Un recorrido de 21 días, acompañado de herramientas para tu alimentación, movimiento, descanso y registro.
      </p>

      <div ref={wrapRef} role="img" aria-label={ALT} className="relative mx-auto mt-6 overflow-hidden rounded-[18px] border border-[#C8A870]/20 bg-[#090807]" style={{
      width: 'min(78vw, 340px)',
      aspectRatio: '9 / 16',
      boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
    }} onClick={() => {
      const v = videoRef.current;
      if (!v || !armed || failed) return;
      if (v.paused) {
        userPausedRef.current = false;
        blockedRef.current = false;
        inViewRef.current = true;
        tryPlay(true);
      } else {
        userPausedRef.current = true;
        v.pause();
      }
    }} data-id="element-152" data-mp="2j7g44a8jkk4u">
        {failed ? <img src={poster} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-153" data-mp="1o8fxloz1wyqg" /> : <video ref={videoRef} src={armed ? sources[srcIdx] : undefined} poster={poster} muted loop playsInline preload={armed ? 'auto' : 'none'} disablePictureInPicture disableRemotePlayback tabIndex={-1} aria-hidden="true" controls={false} className="absolute inset-0 h-full w-full object-cover" onCanPlay={() => {
        note('listo para reproducir');
        tryPlay();
      }} onLoadedMetadata={() => note('metadatos cargados')} onPlaying={() => note('reproduciendo')} onWaiting={() => note('esperando datos')} onStalled={() => note('sin datos (stalled)')} onPlay={() => {
        if (!playedOnceRef.current) {
          playedOnceRef.current = true;
          trackEvent('demo_video_play', {
            section: 'como_funciona'
          });
        }
      }} onError={handleError} data-id="element-154" data-mp="1k85jfp13v20i" />}
      </div>
      {debug && <p className="mt-3 break-words font-mono text-[11px] leading-snug text-[#F0E8D8]/55" data-id="element-155" data-mp="1txplr1r0jato">{dbg || 'diagnóstico activo · esperando…'}</p>}
    </div>;
}